<template>
  <div
    class="min-h-screen flex items-center justify-center px-2 sm:px-4 pt-8 sm:pt-48 pb-8 lg:pb-20"
  >
    <div class="max-w-sm sm:max-w-lg lg:max-w-2xl w-full">
      <!-- Application Under Review -->
      <div
        v-if="applicationStatus === 'pending'"
        class="bg-white rounded-2xl sm:rounded-3xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8 lg:p-10 text-center border-2 sm:border-4 border-black"
      >
        <div class="mb-6 sm:mb-8">
          <div
            class="mx-auto w-20 h-20 sm:w-18 sm:h-18 lg:w-20 lg:h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4 sm:mb-6"
          >
            <svg
              class="w-10 h-10 sm:w-9 sm:h-9 lg:w-10 lg:h-10 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <h2 class="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
            Application Under Review
          </h2>
        </div>

        <p class="text-base sm:text-lg text-gray-700 mb-5 sm:mb-8 leading-relaxed px-2 sm:px-0">
          Your tutor application is
          <span class="font-semibold text-blue-600">still pending review</span> by our academic
          team.
          <br class="block sm:hidden" />
          <span class="text-sm sm:text-base block mt-2 sm:inline sm:mt-0"
            >Please revisit this page again for updates or stay tuned to your email for
            notifications regarding your application status.</span
          >
        </p>

        <div
          class="bg-blue-50 border-2 border-blue-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-5 sm:mb-8"
        >
          <div class="flex items-center justify-center">
            <svg
              class="w-6 h-6 sm:w-6 sm:h-6 text-blue-600 mr-3 sm:mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              ></path>
            </svg>
            <span class="text-sm sm:text-base text-blue-800 font-semibold"
              >We'll notify you via email once reviewed</span
            >
          </div>
        </div>

        <button
          @click="refreshStatus"
          :disabled="isRefreshing"
          class="w-full bg-blue-600 text-white border-2 border-blue-600 py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg font-semibold flex items-center justify-center"
        >
          <span v-if="isRefreshing" class="flex items-center justify-center">
            <svg
              class="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Refreshing...
          </span>
          <span v-else class="flex items-center justify-center font-semibold">
            <svg
              class="w-4 h-4 sm:w-5 sm:h-5 text-white mr-2 sm:mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              ></path>
            </svg>
            Refresh Status
          </span>
        </button>
      </div>

      <!-- Resubmitted Documents Under Review -->
      <div
        v-if="applicationStatus === 'resubmitted_under_review'"
        class="bg-white rounded-2xl sm:rounded-3xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8 lg:p-10 text-center border-2 sm:border-4 border-black"
      >
        <div class="mb-6 sm:mb-8">
          <div
            class="mx-auto w-20 h-20 sm:w-18 sm:h-18 lg:w-20 lg:h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4 sm:mb-6"
          >
            <svg
              class="w-10 h-10 sm:w-9 sm:h-9 lg:w-10 lg:h-10 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <h2 class="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
            Resubmitted Documents Under Review
          </h2>
        </div>

        <p class="text-base sm:text-lg text-gray-700 mb-5 sm:mb-8 leading-relaxed px-2 sm:px-0">
          Your resubmitted documents are
          <span class="font-semibold text-blue-600">currently being reviewed</span> by our team.
          <br class="block sm:hidden" />
          <span class="text-sm sm:text-base block mt-2 sm:inline sm:mt-0"
            >You will be notified once the review is complete. Please check back for updates or stay tuned to your email for notifications.</span
          >
        </p>

        <div
          class="bg-blue-50 border-2 border-blue-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-5 sm:mb-8"
        >
          <div class="flex items-center justify-center">
            <svg
              class="w-6 h-6 sm:w-6 sm:h-6 text-blue-600 mr-3 sm:mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span class="text-sm sm:text-base text-blue-800 font-semibold"
              >We'll notify you via email once reviewed</span
            >
          </div>
        </div>

        <button
          @click="refreshStatus"
          :disabled="isRefreshing"
          class="w-full bg-blue-600 text-white border-2 border-blue-600 py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg font-semibold flex items-center justify-center"
        >
          <span v-if="isRefreshing" class="flex items-center justify-center">
            <svg
              class="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Refreshing...
          </span>
          <span v-else>Refresh Status</span>
        </button>
      </div>

      <!-- Document Resubmission Interface -->
      <div
        v-if="applicationStatus === 'document_issue'"
        class="bg-white rounded-2xl sm:rounded-3xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8 lg:p-10 border-2 sm:border-4 border-black"
      >
        <!-- Header -->
        <div class="mb-6 sm:mb-8 text-center">
          <div
            class="mx-auto w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 bg-orange-100 rounded-full flex items-center justify-center mb-4 sm:mb-6"
          >
            <svg
              class="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 text-orange-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              ></path>
            </svg>
          </div>
          <h2 class="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
            Resubmit Documents
          </h2>
          <p class="text-base sm:text-lg text-gray-700 leading-relaxed px-2 sm:px-0">
            Please resubmit the required documents below
          </p>
        </div>

        <!-- Loading State -->
        <div v-if="isLoadingDocuments" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <span class="text-gray-600">Loading required documents...</span>
        </div>

        <!-- Error State -->
        <div v-else-if="documentsError" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div class="flex">
            <svg class="h-5 w-5 text-red-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Error Loading Documents</h3>
              <p class="text-sm text-red-700 mt-1">{{ documentsError }}</p>
              <button
                @click="fetchRequiredDocuments"
                class="mt-2 text-sm font-medium text-red-800 hover:text-red-900 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>

        <!-- Success Message -->
        <div v-if="showSuccessMessage" class="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div class="flex">
            <svg class="h-5 w-5 text-green-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <div class="ml-3">
              <p class="text-sm font-medium text-green-800">{{ successMessage }}</p>
            </div>
          </div>
        </div>

        <!-- Progress Indicator -->
        <div v-if="requiredDocuments.length > 0" class="mb-6 bg-gray-50 rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-sm font-semibold text-gray-900">Progress</h3>
            <span class="text-sm text-gray-600">
              {{ completedDocuments.length }} of {{ requiredDocuments.length }} completed
            </span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="bg-orange-600 h-2 rounded-full transition-all duration-300"
              :style="{ width: progressPercentage + '%' }"
            ></div>
          </div>
        </div>

        <!-- Admin Note -->
        <div v-if="adminNote" class="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <h3 class="font-medium text-blue-800 mb-2 flex items-center">
            <svg class="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            Additional Notes from Admin:
          </h3>
          <p class="text-sm text-blue-700">{{ adminNote }}</p>
        </div>

        <!-- Documents Grid -->
        <div v-if="requiredDocuments.length > 0" class="grid gap-4 sm:grid-cols-2 mb-6">
          <div
            v-for="document in requiredDocuments"
            :key="document.id"
            class="bg-gray-50 rounded-lg border p-4 hover:bg-gray-100 transition-colors"
          >
            <!-- Document Header -->
            <div class="flex items-start justify-between mb-3">
              <div class="flex-1">
                <h4 class="font-semibold text-gray-900 text-sm mb-1">
                  {{ document.name }}
                </h4>
                <p v-if="document.description" class="text-xs text-gray-600">
                  {{ document.description }}
                </p>
              </div>
              <span
                v-if="document.status === 'resubmitted'"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2"
              >
                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                Submitted
              </span>
              <span
                v-else
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 ml-2"
              >
                Required
              </span>
            </div>

            <!-- Current File Display -->
            <div v-if="document.currentFile" class="mb-3 p-2 bg-white rounded border">
              <div class="flex items-center justify-between">
                <div class="flex items-center min-w-0">
                  <svg class="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                  </svg>
                  <span class="text-xs text-gray-700 truncate">{{ document.currentFile.name }}</span>
                </div>
                <a
                  :href="document.currentFile.url"
                  target="_blank"
                  class="text-blue-600 hover:text-blue-800 text-xs font-medium ml-2 flex-shrink-0"
                >
                  View
                </a>
              </div>
            </div>

            <!-- File Upload -->
            <div class="relative">
              <input
                :id="`file-${document.id}`"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                @change="(event) => handleFileSelect(event, document.id)"
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                :disabled="uploadingDocuments.has(document.id)"
              />
              <div
                class="border-2 border-dashed rounded p-3 text-center transition-colors"
                :class="{
                  'border-orange-400 bg-orange-50': uploadingDocuments.has(document.id),
                  'border-gray-300 hover:border-gray-400': !uploadingDocuments.has(document.id)
                }"
              >
                <div v-if="uploadingDocuments.has(document.id)" class="flex items-center justify-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                  <span class="text-sm text-orange-600">Uploading...</span>
                </div>
                <div v-else class="flex flex-col items-center">
                  <svg class="w-6 h-6 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span class="text-xs text-gray-600">
                    {{ document.status === 'resubmitted' ? 'Replace file' : 'Choose file' }}
                  </span>
                  <span class="text-xs text-gray-500">PDF, JPG, PNG (max 10MB)</span>
                </div>
              </div>
            </div>

            <!-- Upload Error -->
            <div v-if="documentErrors[document.id]" class="mt-2 text-xs text-red-600">
              {{ documentErrors[document.id] }}
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="text-center">
          <button
            @click="handleSubmitDocuments"
            :disabled="!canSubmitDocuments || isSubmittingDocuments"
            class="w-full bg-orange-600 text-white py-3 px-6 rounded-xl hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center"
          >
            <div v-if="isSubmittingDocuments" class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Submitting...
            </div>
            <span v-else>Submit All Documents</span>
          </button>
          
          <p class="text-xs text-gray-600 mt-4">
            Need help? Contact our support team at
            <a href="mailto:support@elevated.edu" class="text-orange-600 hover:underline font-medium">
              support@elevated.edu
            </a>
          </p>
        </div>
      </div>

      <!-- Application Rejected -->
      <div
        v-if="applicationStatus === 'rejected'"
        class="bg-white rounded-2xl sm:rounded-3xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 sm:p-8 lg:p-10 text-center border-2 sm:border-4 border-black"
      >
        <div class="mb-6 sm:mb-8">
          <div
            class="mx-auto w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 bg-red-100 rounded-full flex items-center justify-center mb-4 sm:mb-6"
          >
            <svg
              class="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </div>
          <h2 class="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">
            Application Rejected
          </h2>
        </div>

        <p class="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 leading-relaxed px-2 sm:px-0">
          Unfortunately, your tutor application has been
          <span class="font-bold text-red-600">rejected</span> <br />after careful review by our
          academic team.
        </p>

        <div
          class="bg-red-50 border-2 border-red-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 text-left"
        >
          <h3 class="font-bold text-red-800 mb-3 sm:mb-4 text-base sm:text-lg flex items-center">
            <svg
              class="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.1" />
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="1.5" />
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                fill="none"
                d="M15 9l-6 6m0-6l6 6"
              />
            </svg>
            Rejection Reasons:
          </h3>
          <ul class="text-sm sm:text-base text-red-700 space-y-2 sm:space-y-3">
            <li v-if="rejectionReason">• {{ rejectionReason }}</li>
            <li v-else>
              • <strong>Subject Expertise:</strong> Insufficient demonstration of mastery in
              required tutoring subjects
            </li>
            <li>
              • <strong>Teaching Experience:</strong> Lack of sufficient teaching or tutoring
              experience
            </li>
            <li>
              • <strong>Background Check:</strong> Unable to verify academic credentials with the
              stated institution
            </li>
          </ul>
        </div>

        <div
          class="bg-gray-50 border-2 border-gray-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 text-left"
        >
          <h3 class="font-bold text-gray-800 mb-3 sm:mb-4 text-base sm:text-lg">Next Steps:</h3>
          <ul class="text-sm sm:text-base text-gray-700 space-y-1 sm:space-y-2">
            <li>• You may reapply after 6 months from the rejection date</li>
            <li>• Review our tutor requirements and guidelines before reapplying</li>
            <li>• Contact support if you believe there was an error in the evaluation</li>
          </ul>
        </div>

        <button
          @click="contactSupport"
          class="w-full bg-red-600 text-white py-3 sm:py-4 px-4 sm:px-6 rounded-xl sm:rounded-2xl hover:bg-red-700 transition-colors mb-4 sm:mb-6 text-base sm:text-lg font-semibold flex items-center justify-center"
        >
          <svg
            class="w-4 h-4 sm:w-5 sm:h-5 text-white mr-2 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            ></path>
          </svg>
          Contact Support
        </button>

        <p class="text-xs sm:text-sm text-gray-600 px-2 sm:px-0">
          <strong>Rejection Date:</strong> {{ getRejectionDate() }}<br />
          <strong>Eligible to Reapply:</strong> {{ getReapplyDate() }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { 
  doc, 
  collection, 
  getDocs, 
  updateDoc, 
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { uploadFile } from '@/utils/storage'
import { logDebug, logInfo, logError, logSuccess } from '@/utils/secureLogger'

const router = useRouter();

// Props
interface Props {
  applicationStatus?: string;
  rejectionReason?: string;
  applicationSubmittedAt?: string | any; // Allow Timestamp objects
  applicationReviewedAt?: string | any; // Allow Timestamp objects
}

const props = withDefaults(defineProps<Props>(), {
  applicationStatus: "pending",
  rejectionReason: "",
  applicationSubmittedAt: "",
  applicationReviewedAt: "",
});

// Types for document resubmission
interface RequiredDocument {
  id: string
  name: string
  description?: string
  status: 'pending' | 'resubmitted'
  currentFile?: {
    name: string
    url: string
    uploadedAt: Date
  }
}

// State
const isRefreshing = ref(false);

// Document resubmission state
const isLoadingDocuments = ref(false)
const documentsError = ref('')
const requiredDocuments = ref<RequiredDocument[]>([])
const uploadingDocuments = ref(new Set<string>())
const documentErrors = ref<Record<string, string>>({})
const showSuccessMessage = ref(false)
const successMessage = ref('')
const isSubmittingDocuments = ref(false)

// Document mapping (admin key -> label)
const documentNameMapping: Record<string, string> = {
  transcript: "Official Transcript",
  certificate: "Certificate of Grade",
  diploma: "Diploma",
  governmentId: "Government ID",
  cv: "Curriculum Vitae",
  clearance: "Barangay or NBI Clearance",
  video: "Video Introduction",
  signedContract: "Signed Contract",
};

// Parse required documents and note from rejectionReason
const parsedRequiredDocuments = computed<string[]>(() => {
  if (!props.rejectionReason) return [];
  const m = props.rejectionReason.match(/Document resubmission required:\s*(.+?)\./);
  if (!m) return [];
  return m[1].split(",").map((k) => k.trim()).filter(Boolean);
});

const adminNote = computed<string>(() => {
  if (!props.rejectionReason) return "";
  const m = props.rejectionReason.match(/Note:\s*(.+)$/);
  return m ? m[1].trim() : "";
});


// Document resubmission computed properties
const completedDocuments = computed(() => 
  requiredDocuments.value.filter(doc => doc.status === 'resubmitted')
)

const progressPercentage = computed(() => {
  if (requiredDocuments.value.length === 0) return 0
  return Math.round((completedDocuments.value.length / requiredDocuments.value.length) * 100)
})

const canSubmitDocuments = computed(() => {
  return requiredDocuments.value.length > 0 && 
         completedDocuments.value.length === requiredDocuments.value.length &&
         uploadingDocuments.value.size === 0
})

// Methods
const refreshStatus = async () => {
  isRefreshing.value = true;
  try {
    // Import Firebase functions
    const { auth, db, doc, getDoc } = await import("@/firebase");

    // Get current user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      router.push("/login");
      return;
    }

    // Get user data from Firestore
    const userDocRef = doc(db, "users", currentUser.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();

      // Emit event to parent to update status
      const statusData = {
        application_status: userData.applicationStatus || "pending",
        profile_status: userData.profileStatus || "pending_approval",
        rejection_reason: userData.rejectionReason || "",
        application_submitted_at: userData.applicationDate,
        application_reviewed_at: userData.reviewedAt,
      };

      emit("statusUpdated", statusData);

      // If approved, redirect to dashboard
      if (userData.applicationStatus === "approved" && userData.profileStatus === "approved") {
        router.push("/tutor/sessions");
      }
    }
  } catch (error) {
    logError("Error refreshing status", error);
  } finally {
    isRefreshing.value = false;
  }
};

// Document resubmission methods
const fetchRequiredDocuments = async () => {
  try {
    isLoadingDocuments.value = true
    documentsError.value = ''
    
    // Import Firebase auth to get current user
    const { auth } = await import("@/firebase");
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      documentsError.value = 'User not authenticated'
      return
    }
    
    logDebug('Fetching required documents for user', { userId: currentUser.uid })
    
    // Query the requiredDocuments subcollection
    const requiredDocsRef = collection(db, 'users', currentUser.uid, 'requiredDocuments')
    const querySnapshot = await getDocs(requiredDocsRef)
    
    const documents: RequiredDocument[] = []
    querySnapshot.forEach((doc) => {
      const data = doc.data()
      documents.push({
        id: doc.id,
        name: data.name || 'Unnamed Document',
        description: data.description,
        status: data.status || 'pending',
        currentFile: data.currentFile ? {
          name: data.currentFile.name,
          url: data.currentFile.url,
          uploadedAt: data.currentFile.uploadedAt?.toDate() || new Date()
        } : undefined
      })
    })
    
    requiredDocuments.value = documents
    logInfo('Required documents fetched successfully', { count: documents.length })
    
    if (documents.length === 0) {
      documentsError.value = 'No documents require resubmission at this time.'
    }
  } catch (err) {
    logError('Error fetching required documents', err)
    documentsError.value = 'Failed to load required documents. Please try again.'
  } finally {
    isLoadingDocuments.value = false
  }
}

const handleFileSelect = async (event: Event, documentId: string) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (!file) return
  
  // Clear previous error
  delete documentErrors.value[documentId]
  
  // Validate file
  const maxSize = 10 * 1024 * 1024 // 10MB
  if (file.size > maxSize) {
    documentErrors.value[documentId] = 'File size must be less than 10MB'
    return
  }
  
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
  if (!allowedTypes.includes(file.type)) {
    documentErrors.value[documentId] = 'Only PDF, JPG, and PNG files are allowed'
    return
  }
  
  await uploadDocument(documentId, file)
}

const uploadDocument = async (documentId: string, file: File) => {
  try {
    // Import Firebase auth to get current user
    const { auth } = await import("@/firebase");
    const currentUser = auth.currentUser;
    
    if (!currentUser) return
    
    uploadingDocuments.value.add(documentId)
    logDebug('Starting document upload', { documentId, fileName: file.name })
    
    // Create storage path
    const storagePath = `resubmitted-documents/${currentUser.uid}/`
    const timestamp = Date.now()
    const fileExtension = file.name.split('.').pop()
    const fileName = `${documentId}_${timestamp}.${fileExtension}`
    
    // Upload to Firebase Storage
    const uploadResult = await uploadFile(file, storagePath, fileName)
    
    if (!uploadResult.success || !uploadResult.url) {
      throw new Error(uploadResult.error || 'Upload failed')
    }
    
    // Update Firestore document in requiredDocuments subcollection
    const docRef = doc(db, 'users', currentUser.uid, 'requiredDocuments', documentId)
    await updateDoc(docRef, {
      status: 'resubmitted',
      currentFile: {
        name: file.name,
        url: uploadResult.url,
        uploadedAt: serverTimestamp()
      },
      resubmittedAt: serverTimestamp()
    })

    // Also update the main user document's documents field so admin can see it
    const userRef = doc(db, 'users', currentUser.uid)
    const documentUpdate = {
      [`documents.${documentId}`]: {
        name: file.name,
        url: uploadResult.url,
        type: file.type,
        size: file.size,
        resubmittedAt: serverTimestamp()
      },
      updatedAt: serverTimestamp()
    }
    await updateDoc(userRef, documentUpdate)
    
    // Update local state
    const documentIndex = requiredDocuments.value.findIndex(doc => doc.id === documentId)
    if (documentIndex !== -1) {
      requiredDocuments.value[documentIndex].status = 'resubmitted'
      requiredDocuments.value[documentIndex].currentFile = {
        name: file.name,
        url: uploadResult.url,
        uploadedAt: new Date()
      }
    }
    
    // 🔥 Create audit log for file resubmission
    try {
      const { createAuditLog } = await import('@/services/auditLogService')
      const documentName = requiredDocuments.value.find(d => d.id === documentId)?.name || documentId
      
      await createAuditLog({
        action: 'tutor_applications',
        type: 'document_access',
        details: `Tutor resubmitted document: ${documentName} (${file.name}) upon admin's request`,
        performedById: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email || 'Tutor',
        performedByEmail: currentUser.email || '',
        affectedUserId: currentUser.uid,
        affectedUserName: currentUser.displayName || currentUser.email || 'Tutor',
        affectedUserEmail: currentUser.email || '',
        metadata: {
          documentId,
          documentType: documentName,
          accessType: 'upload',
          fileName: file.name,
          fileSize: file.size
        }
      })
      logInfo('Audit log created for document resubmission', { documentId, fileName: file.name })
    } catch (auditError) {
      logError('Failed to create audit log for document resubmission', auditError)
      // Continue anyway - audit log is not critical for file upload
    }
    
    logSuccess('Document uploaded successfully', { documentId, fileName: file.name })
    showSuccessToast(`${requiredDocuments.value.find(d => d.id === documentId)?.name} uploaded successfully!`)
    
  } catch (err) {
    logError('Error uploading document', err)
    documentErrors.value[documentId] = 'Upload failed. Please try again.'
  } finally {
    uploadingDocuments.value.delete(documentId)
  }
}

const handleSubmitDocuments = async () => {
  if (!canSubmitDocuments.value) return
  
  try {
    // Import Firebase auth to get current user
    const { auth } = await import("@/firebase");
    const currentUser = auth.currentUser;
    
    if (!currentUser) return
    
    isSubmittingDocuments.value = true
    logDebug('Submitting document resubmission')
    
    // Update user's resubmission status
    const userRef = doc(db, 'users', currentUser.uid)
    await updateDoc(userRef, {
      documentResubmissionCompleted: true,
      documentResubmissionCompletedAt: serverTimestamp(),
      applicationStatus: 'resubmitted_under_review', // New status for resubmitted documents
      updatedAt: serverTimestamp()
    })
    
    // 🔥 Create audit log for completing document resubmission
    try {
      const { createAuditLog } = await import('@/services/auditLogService')
      const resubmittedDocuments = requiredDocuments.value
        .filter(doc => doc.status === 'resubmitted')
        .map(doc => doc.name)
        .join(', ')
      
      await createAuditLog({
        action: 'tutor_applications',
        type: 'system_event',
        details: `Tutor completed document resubmission process for documents: ${resubmittedDocuments}`,
        performedById: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email || 'Tutor',
        performedByEmail: currentUser.email || '',
        affectedUserId: currentUser.uid,
        affectedUserName: currentUser.displayName || currentUser.email || 'Tutor',
        affectedUserEmail: currentUser.email || '',
        metadata: {
          eventType: 'document_resubmission_completed',
          systemComponent: 'tutor_application',
          severity: 'medium',
          documentsCount: requiredDocuments.value.filter(doc => doc.status === 'resubmitted').length
        }
      })
      logInfo('Audit log created for completed document resubmission')
    } catch (auditError) {
      logError('Failed to create audit log for completed document resubmission', auditError)
      // Continue anyway - audit log is not critical
    }
    
    logSuccess('Document resubmission completed successfully')
    showSuccessToast('All documents submitted successfully! Your resubmitted documents are now under review.')
    
    // Emit status update to parent
    emit("statusUpdated", {
      application_status: "resubmitted_under_review",
      profile_status: "pending_approval",
      rejection_reason: "",
      application_submitted_at: new Date(),
      application_reviewed_at: null,
    });
    
  } catch (err) {
    logError('Error completing document resubmission', err)
    showSuccessToast('Failed to submit documents. Please try again.', 'error')
  } finally {
    isSubmittingDocuments.value = false
  }
}

const showSuccessToast = (message: string, type: 'success' | 'error' = 'success') => {
  successMessage.value = message
  showSuccessMessage.value = true
  
  setTimeout(() => {
    showSuccessMessage.value = false
  }, 5000)
}


const contactSupport = () => {
  window.location.href = "mailto:support@elevated.edu?subject=Tutor Application Rejection Appeal";
};


const formatDate = (dateInput: string | any) => {
  if (!dateInput) return "N/A";

  let date: Date;

  // Handle Firebase Timestamp objects
  if (dateInput && typeof dateInput === "object" && dateInput.toDate) {
    date = dateInput.toDate();
  } else if (dateInput && typeof dateInput === "object" && dateInput.seconds) {
    // Handle Timestamp-like objects
    date = new Date(dateInput.seconds * 1000);
  } else if (typeof dateInput === "string") {
    date = new Date(dateInput);
  } else {
    return "N/A";
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return "N/A";
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getRejectionDate = () => {
  // Use applicationReviewedAt as the rejection date since that's when the status was updated
  return formatDate(props.applicationReviewedAt);
};

const getReapplyDate = () => {
  if (!props.applicationReviewedAt) return "N/A";

  let reviewDate: Date;

  // Handle Firebase Timestamp objects
  if (
    props.applicationReviewedAt &&
    typeof props.applicationReviewedAt === "object" &&
    props.applicationReviewedAt.toDate
  ) {
    reviewDate = props.applicationReviewedAt.toDate();
  } else if (
    props.applicationReviewedAt &&
    typeof props.applicationReviewedAt === "object" &&
    props.applicationReviewedAt.seconds
  ) {
    // Handle Timestamp-like objects
    reviewDate = new Date(props.applicationReviewedAt.seconds * 1000);
  } else if (typeof props.applicationReviewedAt === "string") {
    reviewDate = new Date(props.applicationReviewedAt);
  } else {
    return "N/A";
  }

  // Check if date is valid
  if (isNaN(reviewDate.getTime())) {
    return "N/A";
  }

  // Add 6 months
  reviewDate.setMonth(reviewDate.getMonth() + 6);

  return reviewDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Emits
const emit = defineEmits<{
  statusUpdated: [data: any];
}>();

// Lifecycle
onMounted(() => {
  // Auto-refresh status every 30 seconds for pending applications
  if (props.applicationStatus === "pending") {
    const interval = setInterval(() => {
      refreshStatus();
    }, 30000);

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }
  
  // Fetch required documents if status is document_issue
  if (props.applicationStatus === "document_issue") {
    fetchRequiredDocuments();
    
    // Auto-refresh required documents every 15 seconds to pick up admin changes
    const docInterval = setInterval(() => {
      fetchRequiredDocuments();
    }, 15000);

    // Cleanup interval on component unmount
    return () => clearInterval(docInterval);
  }
});
</script>

<style scoped>
/* Additional custom styles if needed */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Loading spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>